import { useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import { darken } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import makeStyles from '@mui/styles/makeStyles'
import { useHistory } from 'react-router-dom'
import { MotionFab } from '@components'
import { colors } from '@global/constants'
import { ConfirmEditModal } from '../confirm-edit-modal'

const useStyles = makeStyles(() => ({
  custom: {
    '&&': {
      backgroundColor: colors.blue.main,
      '&:hover': {
        backgroundColor: darken(colors.blue.main, 0.3),
      },
    },
  },
}))

interface Props {
  datasetId: string | number
  currentRowCount: number
  shouldShowEditConfirmModal: boolean
}

export const MAX_EDIT_ROW_COUNT = 5000
export const MAX_EDIT_ROW_COUNT_MSG = `Editing pending submission with > ${MAX_EDIT_ROW_COUNT}k rows is currently not supported`

const DisabledTooltipWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children

const Wrapper = children => (
  <Tooltip title={MAX_EDIT_ROW_COUNT_MSG} data-testid="edit-submission-tooltip">
    <span>{children}</span>
  </Tooltip>
)

/**
 * The edit submission button in the read grid header
 *
 * @param props React props
 * @param {number | string} props.datasetId The id of the selected dataset
 * @param {number} props.currentRowCount The row count for the current dataset
 * @returns
 */
export const EditSubmission = ({
  datasetId,
  currentRowCount,
  shouldShowEditConfirmModal,
}: Props) => {
  const classes = useStyles()
  const history = useHistory()
  const [isConfirmEditModalOpen, setIsConfirmEditModalOpen] = useState(false)
  const disabled = !(currentRowCount <= MAX_EDIT_ROW_COUNT)

  return (
    <>
      <DisabledTooltipWrapper condition={disabled} wrapper={Wrapper}>
        <MotionFab
          disabled={disabled}
          tooltipTitle="Update values and resync this submission with destinations"
          size="medium"
          className={classes.custom}
          onClick={() => {
            if (shouldShowEditConfirmModal) setIsConfirmEditModalOpen(true)
            else handleContinue()
          }}
        >
          <EditIcon data-testid="edit-icon" />
        </MotionFab>
      </DisabledTooltipWrapper>
      {shouldShowEditConfirmModal && (
        <ConfirmEditModal
          open={isConfirmEditModalOpen}
          onClose={() => {
            setIsConfirmEditModalOpen(false)
          }}
          onContinue={handleContinue}
        />
      )}
    </>
  )

  function handleContinue() {
    history.push(`/codes/resubmit/${datasetId}`)
  }
}
